/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '#utils/UNSAFE_classNames';
import { usePressClick } from '#hooks/UNSAFE_usePressClick';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { LinkVariantOptions } from '#UNSAFE_Link/themes/LinkStyles.css';
import { useInteractionStyle } from '#hooks/UNSAFE_useInteractionStyle';
import { LinkRedwoodTheme } from '#UNSAFE_Link/themes/redwood/LinkTheme';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useTestId, TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useDetectTruncation } from '#hooks/PRIVATE_useDetectTruncation';
import { useTooltip } from '#hooks/UNSAFE_useTooltip';
import { useState } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { UnpackSignals } from '#utils/UNSAFE_attributeUtils';
import type { JSX } from 'preact';
import { Text } from '#UNSAFE_Text';

type AriaProps = Pick<
  UnpackSignals<JSX.AriaAttributes>,
  'aria-describedby' | 'aria-label' | 'aria-labelledby'
>;

type Props = {
  /**
   * Sets the URL that the hyperlink points to.
   */
  href: string;

  /**
   * Sets the variant. The default value of this property is theme driven.
   */
  variant?: 'standard' | 'primary' | 'secondary';

  /**
   * Sets the placement.
   */
  placement?: 'standalone' | 'embedded';

  /**
   * Sets the target.
   */
  target?: string;

  /**
   * Triggered when a link is clicked, whether by keyboard, mouse, or touch events.
   */
  onClick?: (event: Event) => void;

  /**
   * Specifies that the link element should be disabled.
   */
  isDisabled?: boolean;

  /**
   * Truncates the link at a specific number of lines and then displays an ellipsis (…) at the end of the last line. Parent of the element needs to have a specific width so the link starts overflowing and produces a truncation.
   */
  lineClamp?: number;

  /**
   * Specifies alternative text to be shown rather than the href.
   */
  children?: string;
} & TestIdProps &
  AriaProps;

const noop = () => {};

/**
 * TruncatingLink component creates a hyperlink that shows a tooltip and ellipsis at the end of text when truncated.
 */
export function TruncatingLink({
  href,
  variant,
  placement = 'standalone',
  target,
  testId,
  'aria-describedby': ariaDescribedBy,
  'aria-label': accessibleLabel,
  'aria-labelledby': ariaLabelledBy,
  onClick,
  isDisabled,
  lineClamp,
  children
}: Props) {
  const { detectTruncationProps, isTextTruncated } = useDetectTruncation<HTMLAnchorElement>();
  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle } = useInteractionStyle();
  const clientHints = getClientHints();
  const { classes } = useComponentTheme<LinkVariantOptions>(LinkRedwoodTheme, {
    needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
    pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
    variant,
    placement,
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    truncatingLinkStyles: placement !== 'embedded' ? 'styles' : 'none',
    truncatingLinkWhiteSpace: placement !== 'embedded' && !lineClamp ? 'nowrap' : 'none',
    safariStyles: clientHints.browser === 'safari' ? 'isSafari' : 'notSafari'
  });
  const { isTabbable } = useTabbableMode();
  const linkClasses = classNames([classes]);
  const { pressProps } = usePressClick(onClick ?? noop);
  const testIdProps = useTestId(testId);
  const [shownText] = useState(children || href);

  const { tooltipContent, tooltipProps } = useTooltip({
    text: shownText,
    isDisabled: !isTextTruncated || isDisabled
  });
  const { 'aria-describedby': notNeeded, ...validTooltipProps } = tooltipProps; //remove aria-describedby to prevent screen readers reading the same text multiple times

  return (
    <>
      <a
        {...mergeProps(
          validTooltipProps,
          detectTruncationProps,
          interactionProps,
          testIdProps,
          !isDisabled ? pressProps : {}
        )}
        tabIndex={isTabbable && !isDisabled ? 0 : -1}
        href={!isDisabled ? href : undefined}
        aria-describedby={ariaDescribedBy}
        aria-label={accessibleLabel}
        aria-labelledby={ariaLabelledBy}
        target={target}
        class={linkClasses}>
        <Text lineClamp={lineClamp} size="md" variant="inherit">
          {shownText}
        </Text>
      </a>
      {tooltipContent}
    </>
  );
}
